import React from 'react';
import type { ValueType } from 'react-select/lib/types';

import * as styles from './styles';
import moment from 'moment';

import type { UUID, Vehicle } from '@cobliteam/common-types';

import {
  isDeviceTypeDashcam,
  isWebDataError,
  isWebDataFetching,
  isWebDataSuccess,
  type MaintenanceOption,
  type MaintenanceType,
  type PastMaintenance,
  type RegisterPastMaintenance,
  type ReminderPrefill,
  type WebData,
} from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';

import {
  CaptionDefault,
  CaptionError,
  Checkbox,
  DefaultInput,
  DescriptionListDefault,
  DescriptionListMedium,
  DescriptionTitle,
  DialogWithHeader,
  type ISelectOptionType,
  Loading,
  NoConnectionIcon,
  NumberInput,
  Placeholder,
  PlaceholderText,
  Select,
  SingleDatePicker,
} from '@cobliteam/base-components';

import VideocamIcon from '@mui/icons-material/Videocam';

import { type InputItems, ItemsInputs } from './components/ItemsInputs/ItemsInputs';
import { RegistrationButtonGroup } from './components/RegistrationButtonGroup/RegistrationButtonGroup';

import { OdometerInfoTooltip } from '../OdometerInfoTooltip/OdometerInfoTooltip';

interface RecurrenceCheckbox {
  checked: boolean;
  name?: string;
}
interface RecurrencesOptions {
  [periodicity: string]: RecurrenceCheckbox;
}

export interface RegistrationModalProps {
  maintenanceItemOptionsWebData: WebData<MaintenanceOption[]>;
  vehiclesWebData: WebData<Vehicle[]>;
  editMaintenance?: PastMaintenance;
  reminderPrefill?: ReminderPrefill;
  onClose: () => void;
  onSaveClick: (maintenance: RegisterPastMaintenance, isFromWarning: boolean) => void;
  onDeleteMaintenance: (maintenance: PastMaintenance) => void;
  onLoadItems: () => void;
}

export const RegistrationModal: React.FC<RegistrationModalProps> = ({
  maintenanceItemOptionsWebData,
  vehiclesWebData,
  editMaintenance,
  reminderPrefill,
  onClose,
  onSaveClick,
  onDeleteMaintenance,
  onLoadItems,
}) => {
  const isMarkAsDone = Boolean(reminderPrefill);
  const isEdition = Boolean(editMaintenance);
  const numberFormatter = new Intl.NumberFormat('pt-BR');

  const [hasValidTotalCost, setHasValidTotalCost] = React.useState(true);
  const [hadAnyChange, setHadAnyChange] = React.useState(false);

  const [maintenanceId, setMaintenanceId] = React.useState<UUID | undefined>(undefined);
  const [selectedVehicleId, setSelectedVehicleId] = React.useState<UUID | undefined>(undefined);
  const [recurrencesMap, setRecurrencesMap] = React.useState<RecurrencesOptions | undefined>(undefined);

  const [isPreventive, setIsPreventive] = React.useState(false);
  const [isCorrective, setIsCorrective] = React.useState(false);

  const [maintenanceDate, setMaintenanceDate] = React.useState<number | undefined>(undefined);
  const [odometer, setOdometer] = React.useState<number | undefined>(undefined);
  const [totalCost, setTotalCost] = React.useState<number | undefined>(undefined);
  const [generalObservations, setGeneralObservations] = React.useState<string | undefined>(undefined);
  const [items, setItems] = React.useState<InputItems>({});

  React.useEffect(() => {
    if (editMaintenance) {
      setMaintenanceId(editMaintenance.id);
      setSelectedVehicleId(editMaintenance.vehicleId);

      const definedRecurrenceNames = editMaintenance.recurrenceNames || {};
      const definedPeriodicitiesInMeters = editMaintenance.periodicitiesInMeters || [];
      const editRecurrencesMap = definedPeriodicitiesInMeters.reduce(
        (acc, rec) => ({
          ...acc,
          [rec]: {
            checked: true,
            name: definedRecurrenceNames[rec] || undefined,
          },
        }),
        {} as RecurrencesOptions
      );

      setRecurrencesMap(Object.keys(editRecurrencesMap).length > 0 ? editRecurrencesMap : undefined);

      setIsCorrective(editMaintenance.types.includes('corrective'));
      setIsPreventive(editMaintenance.types.includes('preventive'));

      setMaintenanceDate(editMaintenance.date);
      setOdometer(editMaintenance.odometerInMeters ? Math.round(editMaintenance.odometerInMeters / 1000) : undefined);
      setTotalCost(editMaintenance.totalCost);
      setGeneralObservations(editMaintenance.generalObservations);

      if (editMaintenance.items && editMaintenance.items.length > 0) {
        const editMaintenanceItems = {} as InputItems;
        editMaintenance.items.forEach((item) => (editMaintenanceItems[item.id] = { ...item }));
        setItems(editMaintenanceItems);
      }
    } else if (reminderPrefill) {
      setSelectedVehicleId(reminderPrefill.vehicleId);
      setRecurrencesMap(reminderPrefill.recurrences);
      setIsPreventive(true);
    }

    if (!isWebDataSuccess(maintenanceItemOptionsWebData)) {
      onLoadItems();
    }
  }, []);

  const getModalTitle = () => {
    if (isEdition) {
      return 'Editar manutenção feita';
    }
    return isMarkAsDone ? 'Marcar como feito' : 'Cadastrar manutenção feita';
  };

  const canBeSubmited = () => {
    const hasVehicle = Boolean(selectedVehicleId);
    const hasRecurrences = !(recurrencesMap && Object.keys(recurrencesMap).every((key) => !recurrencesMap[key].checked));
    const hasType = isCorrective || isPreventive;
    const hasDate = Boolean(maintenanceDate);
    const hasOdometer = odometer !== undefined;
    const hasItems = Object.values(items).every((i) => i.name);

    return hadAnyChange && hasValidTotalCost && hasVehicle && hasRecurrences && hasType && hasDate && hasOdometer && hasItems;
  };

  const handleConfirm = () => {
    const recurrences =
      (recurrencesMap &&
        Object.keys(recurrencesMap)
          .filter((recurrence) => recurrencesMap[recurrence].checked)
          .map((recurrence) => Number.parseInt(recurrence, 10))) ||
      [];

    const recurrenceNames =
      recurrencesMap &&
      recurrences.reduce(
        (acc, rec) => ({
          ...acc,
          [rec]: recurrencesMap[rec.toString()].name || '',
        }),
        {} as {
          [recurrenceValue: number]: string;
        }
      );

    const types = [...(isPreventive ? ['preventive'] : []), ...(isCorrective ? ['corrective'] : [])];

    const maintenanceItems = Object.values(items).flatMap((item) =>
      item.name
        ? [
            {
              name: item.name,
              price: item.price,
              remark: item.remark,
              id: item.id,
            },
          ]
        : []
    );

    const maintenancesHistory = {
      id: maintenanceId,
      vehicleId: selectedVehicleId || '',
      date: maintenanceDate || 0,
      totalCost: totalCost || 0,
      odometerInMeters: (odometer || 0) * 1000,
      generalObservations,
      types: types as MaintenanceType[],
      items: maintenanceItems,
      periodicitiesInMeters: recurrences.length > 0 ? recurrences : undefined,
      recurrenceNames: Object.keys(recurrenceNames || {}).length > 0 ? recurrenceNames : undefined,
    };

    const isComplete =
      maintenancesHistory.totalCost > 0 && maintenancesHistory.generalObservations && maintenancesHistory.generalObservations.length > 0 && maintenancesHistory.items.length > 0;

    if (isEdition && isComplete && !isMarkAsDone) {
      Analytics.track('Maintenance Registry Edit With Complete Information Saved');
    } else if (isEdition && !isComplete && !isMarkAsDone) {
      Analytics.track('Maintenance Registry Edit Saved');
    } else if (isComplete && !isMarkAsDone) {
      Analytics.track('Maintenance New Registry With Complete Information Saved');
    } else if (isComplete && isMarkAsDone) {
      Analytics.track('Maintenance Mark As Done Registry With Complete Information Saved');
    } else if (isMarkAsDone) {
      Analytics.track('Maintenance Mark As Done Registry Saved');
    } else {
      Analytics.track('Maintenance New Registry Saved');
    }

    onSaveClick(maintenancesHistory, isMarkAsDone);
  };

  const handleDelete = (em: PastMaintenance) => {
    Analytics.track('Maintenance Delete Button Clicked');
    onDeleteMaintenance({ ...em });
  };

  const handleVehicleChange = (selected: ValueType<ISelectOptionType>) => {
    const { value } = selected as ISelectOptionType;

    setSelectedVehicleId(value);
    setHadAnyChange(true);
  };

  const handleRecurrencesChange = (periodicity: string) => {
    const recurrence = recurrencesMap?.[periodicity];
    const checked = recurrence ? !recurrence.checked : false;
    setRecurrencesMap({
      ...recurrencesMap,
      [periodicity]: { ...recurrence, checked },
    });
  };

  const handlePreventiveChange = () => {
    setIsPreventive(!isPreventive);
    setHadAnyChange(true);
  };

  const handleCorrectiveChange = () => {
    setIsCorrective(!isCorrective);
    setHadAnyChange(true);
  };

  const handleMaintenanceDateChange = (beginDayInMillis: number) => {
    setMaintenanceDate(beginDayInMillis);
    setHadAnyChange(true);
  };

  const handleOdometerChange = (newValue?: number) => {
    setOdometer(newValue);
    setHadAnyChange(true);
  };

  const handleTotalCostChange = (newValue?: number) => {
    const itemsCost = Object.values(items).reduce((acc, i) => acc + (i.price || 0), 0);

    // To handle problems like 0.1 + 0.2 = 0.30000000000000004
    const itemsCostPrecision = Math.round(itemsCost * 1e2) / 1e2;

    setTotalCost(newValue);
    setHasValidTotalCost(itemsCostPrecision <= (newValue || 0));
    setHadAnyChange(true);
  };

  const handleObservationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setGeneralObservations(event.currentTarget.value);
    setHadAnyChange(true);
  };

  const handleItemsChange = (newItems: InputItems) => {
    const itemsCost = Object.values(newItems).reduce((acc, i) => acc + (i.price || 0), 0);

    // To handle problems like 0.1 + 0.2 = 0.30000000000000004
    const itemsCostPrecision = Math.round(itemsCost * 1e2) / 1e2;

    setItems(newItems);
    setHasValidTotalCost(itemsCostPrecision <= (totalCost || 0));
    setHadAnyChange(true);
  };

  const renderVehicleSelect = (vehicles: Vehicle[]) => {
    const options = vehicles
      .map((vehicle) => ({
        label: vehicle.label,
        value: vehicle.id,
        Icon: vehicle.deviceType && isDeviceTypeDashcam(vehicle.deviceType) ? <VideocamIcon className={styles.dashcamOptionIcon} /> : undefined,
      }))
      .sort((a, b) => (b.label < a.label ? 1 : -1));

    return (
      <>
        <DescriptionListMedium>Veículo</DescriptionListMedium>
        <Select
          placeholder='Selecionar veículo'
          className={styles.select}
          value={options.find((v) => v.value === selectedVehicleId)}
          onChange={handleVehicleChange}
          maxMenuHeight={130}
          options={options}
          isDisabled={isEdition || isMarkAsDone}
        />
      </>
    );
  };

  const renderRecurrenceCheckbox = (recurrence: [string, RecurrenceCheckbox], index: number) => (
    <div key={index} className={styles.recurrence}>
      <Checkbox
        checked={recurrence[1].checked}
        label={`${numberFormatter.format(Number.parseInt(recurrence[0], 10) / 1000)} km`}
        disabled={!isMarkAsDone}
        onChange={() => handleRecurrencesChange(recurrence[0])}
      />
      <DescriptionListDefault ellipsis>{recurrence[1].name || <PlaceholderText>Nenhuma descrição</PlaceholderText>}</DescriptionListDefault>
    </div>
  );

  const firstSelectableMaintenanceDay = () => {
    const totalDistanceInMetersOldestFullDay = moment().subtract(364, 'days').startOf('day');
    const reminderCreatedAt = reminderPrefill && moment(reminderPrefill.createdAt);

    if (reminderCreatedAt?.isAfter(totalDistanceInMetersOldestFullDay)) {
      return reminderCreatedAt.toDate();
    }
    return totalDistanceInMetersOldestFullDay.toDate();
  };

  return (
    <DialogWithHeader
      open
      maxWidth='lg'
      title={getModalTitle()}
      onCloseButtonClick={onClose}
      scroll='body'
      actions={
        <RegistrationButtonGroup
          buttonText={isEdition ? 'Salvar' : 'Cadastrar'}
          canSubmit={canBeSubmited()}
          onCancel={onClose}
          onConfirm={handleConfirm}
          onDelete={editMaintenance ? () => handleDelete(editMaintenance) : undefined}
        />
      }
    >
      {isWebDataSuccess(maintenanceItemOptionsWebData) && isWebDataSuccess(vehiclesWebData) && (
        <div className={styles.modal}>
          <div>{renderVehicleSelect(vehiclesWebData.data)}</div>

          {recurrencesMap !== undefined && (
            <div>
              <DescriptionTitle id='recurrences'>Recorrências</DescriptionTitle>
              <div className={styles.recurrences}>{Object.entries(recurrencesMap).map(renderRecurrenceCheckbox)}</div>
            </div>
          )}

          <div>
            <DescriptionTitle>Informações gerais</DescriptionTitle>
            <div className={styles.checkboxes}>
              <Checkbox checked={isPreventive} label='Preventiva' onChange={handlePreventiveChange} disabled={isEdition} />
              <Checkbox checked={isCorrective} label='Corretiva' onChange={handleCorrectiveChange} disabled={isEdition} />
            </div>
            <div className={styles.infoSection}>
              <div className={styles.infoBlock}>
                <DescriptionListMedium>Data da manutenção</DescriptionListMedium>
                <SingleDatePicker
                  disabled={isEdition}
                  placeholder='dd/mm/aaaa'
                  selectedDateInMillis={maintenanceDate}
                  handleDayChange={handleMaintenanceDateChange}
                  disabledDays={{
                    after: moment().toDate(),
                    before: firstSelectableMaintenanceDay(),
                  }}
                />
              </div>
              <div className={styles.infoBlock}>
                <DescriptionListMedium>
                  Odômetro na data
                  <OdometerInfoTooltip />
                </DescriptionListMedium>
                <NumberInput placeholder='km' value={odometer} disabled={isEdition} onNumberChange={handleOdometerChange} />
              </div>
              <div className={styles.infoBlock}>
                <DescriptionListMedium>Custo total</DescriptionListMedium>
                &nbsp;
                <CaptionDefault>(opcional)</CaptionDefault>
                <NumberInput placeholder='R$' value={totalCost} precision={2} error={!hasValidTotalCost} onNumberChange={handleTotalCostChange} />
                {!hasValidTotalCost && <CaptionError className={styles.errorLabel}>Custo total inválido</CaptionError>}
              </div>
              <div className={styles.infoBlock}>
                <DescriptionListMedium>Observações gerais</DescriptionListMedium>
                &nbsp;
                <CaptionDefault>(opcional)</CaptionDefault>
                <DefaultInput type='text' placeholder='Observações gerais' value={generalObservations} onChange={handleObservationsChange} />
              </div>
            </div>
          </div>
          <div>
            <DescriptionTitle>O que foi feito</DescriptionTitle>
            &nbsp;
            <CaptionDefault>(opcional)</CaptionDefault>
            <ItemsInputs items={items} maintenanceItemOptions={maintenanceItemOptionsWebData.data} onItemsChange={handleItemsChange} />
          </div>
        </div>
      )}

      {(isWebDataError(vehiclesWebData) || isWebDataError(maintenanceItemOptionsWebData)) && (
        <Placeholder className={styles.placeholderWithoutTextButton} text='Ops! Ocorreu um erro no sistema.' Icon={NoConnectionIcon} />
      )}

      {(isWebDataFetching(vehiclesWebData) || isWebDataFetching(maintenanceItemOptionsWebData)) && <Loading className={styles.loading} />}
    </DialogWithHeader>
  );
};
